import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
	AffiliateDashboardData,
	AffiliateDashboardWorkspace,
	normalizeAffiliateSection,
} from "./AffiliateDashboardWorkspace";
import type { AffiliateSupportDetails } from "./AffiliateDashboardWorkspace";
import {
	apiServices,
	getApiErrorMessage,
} from "../../../../core/utilities/ApiServices";

const emptyDashboard: AffiliateDashboardData = {
	status: "",
	code: "",
	promoCode: "",
	link: "",
	assignedCode: "",
	assignedLink: "",
	customCodeRequest: null,
	disclosureText: "",
	payoutSetupStatus: "",
	payoutSetupMessage: "",
	nextPayoutDate: null,
	minimumPayoutProgress: {
		minimumAmount: 100,
		payableAmount: 0,
		percent: 0,
		remainingAmount: 100,
	},
	referralJourney: {
		clickedLink: 0,
		signedUp: 0,
		subscribed: 0,
		eligibleCommission: 0,
	},
	profile: {
		displayName: "",
		contactEmail: "",
		payoutPayPalEmail: "",
	},
	stats: {
		clicks: 0,
		signups: 0,
		paidUsers: 0,
		grossTrackedAmount: 0,
		pendingEarnings: 0,
		payableEarnings: 0,
		paidEarnings: 0,
		blockedEarnings: 0,
	},
	performance: { daily: [], weekly: [], monthly: [] },
	trafficSources: [],
	campaignLinks: [],
	marketingKit: {
		banners: [],
		captions: [],
		emailCopy: [],
		disclosureCopy: [],
		videoScripts: [],
		videoAds: [],
		postingRules: [],
	},
	payoutHistory: [],
	commissionRules: [],
	notifications: [],
	tierProgress: {
		currentTier: "Starter",
		nextTier: "Top tier",
		paidUsers: 0,
		targetPaidUsers: 1,
		percent: 0,
	},
};

export const AffiliateOnlyDashboardScreen = () => {
	const { section } = useParams();
	const [params] = useSearchParams();
	const token = params.get("token") || "";
	const [dashboard, setDashboard] = useState<AffiliateDashboardData | null>(
		null,
	);
	const [payoutPayPalEmail, setPayoutPayPalEmail] = useState("");
	const [campaignLabel, setCampaignLabel] = useState("");
	const [campaignSource, setCampaignSource] = useState("YouTube");
	const [customCode, setCustomCode] = useState("");
	const [supportSubject, setSupportSubject] = useState("");
	const [supportMessage, setSupportMessage] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");

	const loadDashboard = useCallback(async () => {
		if (!token) {
			setError("Affiliate dashboard access is missing.");
			setLoading(false);
			return;
		}

		setLoading(true);
		setError("");
		try {
			const response = await apiServices.get(
				"/affiliate/public/affiliate-only/dashboard",
				{ params: { token } },
			);
			const nextDashboard = { ...emptyDashboard, ...response.data };
			setDashboard(nextDashboard);
			setPayoutPayPalEmail(response.data?.profile?.payoutPayPalEmail || "");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not load affiliate dashboard."));
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadDashboard();
	}, [loadDashboard]);

	const savePayoutEmail = async () => {
		if (!payoutPayPalEmail.includes("@")) {
			setError("Please add a valid PayPal payout email.");
			return;
		}

		setSaving(true);
		setMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/public/affiliate-only/payout-setup/paypal-email",
				{ payoutPayPalEmail },
				{ params: { token } },
			);
			const nextDashboard = { ...emptyDashboard, ...response.data };
			setDashboard(nextDashboard);
			setPayoutPayPalEmail(response.data?.profile?.payoutPayPalEmail || "");
			setMessage("PayPal payout email saved.");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not save PayPal payout email."));
		} finally {
			setSaving(false);
		}
	};

	const createCampaignLink = async () => {
		if (!campaignLabel.trim()) {
			setError("Please add a campaign label.");
			return;
		}
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/public/affiliate-only/campaign-links",
				{ label: campaignLabel, source: campaignSource },
				{ params: { token } },
			);
			const nextDashboard = { ...emptyDashboard, ...response.data };
			setDashboard(nextDashboard);
			setCampaignLabel("");
			setMessage("Campaign link created.");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not create campaign link."));
		} finally {
			setSaving(false);
		}
	};

	const requestCustomCode = async () => {
		if (!customCode.trim()) {
			setError("Please add the code you want.");
			return;
		}
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/public/affiliate-only/custom-code-request",
				{ code: customCode },
				{ params: { token } },
			);
			const nextDashboard = { ...emptyDashboard, ...response.data };
			setDashboard(nextDashboard);
			setCustomCode("");
			setMessage(
				response.data?.customCodeRequest?.status === "needs_review"
					? "Code request sent for review."
					: "Personal code saved.",
			);
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not request that code."));
		} finally {
			setSaving(false);
		}
	};

	const buildSupportIssue = (details: AffiliateSupportDetails) =>
		[
			`Support type: ${supportSubject}`,
			`Priority: ${details.priority}`,
			details.campaign ? `Campaign/code/link: ${details.campaign}` : "",
			`Preferred reply: ${details.contactPreference}`,
			"",
			supportMessage.trim(),
		]
			.filter(Boolean)
			.join("\n");

	const sendSupportRequest = async (details: AffiliateSupportDetails) => {
		if (!supportSubject.trim() || supportMessage.trim().length < 10) {
			setError("Please add a subject and a short message.");
			return;
		}
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/public/affiliate-only/support-requests",
				{ subject: supportSubject, message: buildSupportIssue(details) },
				{ params: { token } },
			);
			const nextDashboard = { ...emptyDashboard, ...response.data };
			setDashboard(nextDashboard);
			setSupportSubject("");
			setSupportMessage("");
			setMessage("Support request sent.");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not send support request."));
		} finally {
			setSaving(false);
		}
	};

	return (
		<AffiliateDashboardWorkspace
			data={dashboard}
			section={normalizeAffiliateSection(section)}
			basePath="/affiliate-dashboard"
			contextLabel="Affiliate-only dashboard"
			loading={loading}
			error={error}
			message={message}
			payoutPayPalEmail={payoutPayPalEmail}
			campaignLabel={campaignLabel}
			campaignSource={campaignSource}
			customCode={customCode}
			supportSubject={supportSubject}
			supportMessage={supportMessage}
			saving={saving}
			onPayoutPayPalEmailChange={setPayoutPayPalEmail}
			onCampaignLabelChange={setCampaignLabel}
			onCampaignSourceChange={setCampaignSource}
			onCustomCodeChange={setCustomCode}
			onSupportSubjectChange={setSupportSubject}
			onSupportMessageChange={setSupportMessage}
			onSavePayoutEmail={savePayoutEmail}
			onCreateCampaignLink={createCampaignLink}
			onRequestCustomCode={requestCustomCode}
			onSendSupportRequest={sendSupportRequest}
		/>
	);
};
